import type { ReactNode } from 'react';

import type { Progression, Signal } from '../api/types';

/** Petits éléments partagés : anneaux, badges, jauges, chiffres, messages. */

const LIBELLES_ANNEAU: Record<string, string> = {
  adopt: 'Adopt',
  trial: 'Trial',
  assess: 'Assess',
  hold: 'Hold',
};

export function Anneau({ anneau }: { anneau: string }): JSX.Element {
  return (
    <span className={`anneau anneau--${anneau}`}>
      <span className="anneau__point" aria-hidden="true" />
      {LIBELLES_ANNEAU[anneau] ?? anneau}
    </span>
  );
}

export function Badge({
  variante,
  children,
  titre,
}: {
  variante?: string;
  children: ReactNode;
  titre?: string;
}): JSX.Element {
  return (
    <span className={`badge${variante ? ` badge--${variante}` : ''}`} title={titre}>
      {children}
    </span>
  );
}

const LIBELLES_SIGNAL: Record<Signal, string> = {
  risque: 'Risk',
  dette: 'Debt',
  inconnu: 'No data',
  // « No signal » et non « Covered » : l'absence d'alerte ne dit pas que la
  // technologie est couverte, seulement qu'aucune règle ne s'est déclenchée.
  ok: 'No signal',
};

const EXPLICATIONS_SIGNAL: Record<Signal, string> = {
  risque: 'In adopt with nobody at level 4 or above: a delivery risk.',
  dette: 'In hold but still widely practised: technical debt.',
  inconnu: 'Nobody has declared anything on this technology yet — a blind spot, not a clean bill of health.',
  ok: 'No rule triggered. Coverage exists but has not been judged sufficient or insufficient.',
};

export function BadgeSignal({ signal }: { signal: Signal }): JSX.Element {
  return (
    <Badge variante={signal} titre={EXPLICATIONS_SIGNAL[signal]}>
      {LIBELLES_SIGNAL[signal]}
    </Badge>
  );
}

export function Jauge({ progression }: { progression: Progression }): JSX.Element {
  const complete = progression.total > 0 && progression.repondu >= progression.total;
  return (
    <div
      className="jauge"
      role="progressbar"
      aria-valuenow={progression.pourcentage}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${progression.repondu} of ${progression.total} answered`}
    >
      <div
        className={`jauge__barre${complete ? ' jauge__barre--complete' : ''}`}
        style={{ width: `${progression.pourcentage}%` }}
      />
    </div>
  );
}

export function Chiffre({
  valeur,
  libelle,
  petit,
}: {
  valeur: ReactNode;
  libelle: string;
  petit?: boolean;
}): JSX.Element {
  return (
    <div className="chiffre">
      <span className={`chiffre__valeur${petit ? ' chiffre__valeur--petit' : ''}`}>{valeur}</span>
      <span className="chiffre__libelle">{libelle}</span>
    </div>
  );
}

/**
 * Les appartenances d'une personne. Quelqu'un peut être dans une douzaine de
 * regroupements — chapitres, projets, lieux : les afficher tous déborde de la
 * cellule. On en montre quelques-uns, le reste se compte, et le titre porte la
 * liste entière.
 */
export function Equipes({
  equipes,
  max = 3,
}: {
  equipes: readonly { id?: string; nom: string }[];
  max?: number;
}): JSX.Element {
  if (equipes.length === 0) return <span className="discret">—</span>;

  const noms = equipes.map((e) => e.nom);
  const visibles = noms.slice(0, max);
  const reste = noms.length - visibles.length;

  return (
    <span className="equipes" title={noms.join(', ')}>
      {visibles.map((nom, index) => (
        <span key={nom} className="equipes__nom">
          {nom}
          {index < visibles.length - 1 || reste > 0 ? ',' : ''}
        </span>
      ))}
      {reste > 0 ? <span className="equipes__reste">+{reste}</span> : null}
    </span>
  );
}

export function Message({
  variante = 'info',
  children,
}: {
  variante?: 'info' | 'attention' | 'erreur' | 'succes';
  children: ReactNode;
}): JSX.Element {
  return <div className={`message message--${variante}`}>{children}</div>;
}

export function Chargement({ quoi = 'Loading' }: { quoi?: string }): JSX.Element {
  return (
    <p className="chargement" role="status">
      {quoi}…
    </p>
  );
}

export function Vide({ children }: { children: ReactNode }): JSX.Element {
  return <p className="vide">{children}</p>;
}

export function Fleche(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}

const FORMAT_DATE = new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'long',
  timeZone: 'Africa/Tunis',
});

const FORMAT_DATE_COURTE = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Africa/Tunis',
});

/**
 * Jour civil au fuseau de l'entreprise, au format `AAAA-MM-JJ` qu'attend un
 * `<input type="date">`. Découper les dix premiers caractères de l'ISO donnait le
 * jour UTC : une date stockée à 00:00 à Tunis y devient 23:00 la veille, et
 * l'écran affichait un jour de moins que le tableau.
 */
export function jourCivil(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Africa/Tunis',
  }).format(new Date(iso));
}

export function dateLongue(iso: string | null | undefined): string {
  if (!iso) return '—';
  return FORMAT_DATE.format(new Date(iso));
}

export function dateCourte(iso: string | null | undefined): string {
  if (!iso) return '—';
  return FORMAT_DATE_COURTE.format(new Date(iso));
}

export function joursRestants(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const reste = new Date(iso).getTime() - Date.now();
  return Math.ceil(reste / 86_400_000);
}
